
import { useState, useEffect, useCallback } from 'react';
import { ethers, EventLog } from 'ethers';
import { User, UserRole } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { API_BASE_URL } from '@/config';
import { useLogger } from '@/contexts/LogContext';
import { LogLevel, LogCategory } from '@/types/logs';
import { useWallet } from '@/contexts/WalletContext';
import { contractAddresses } from '@/data/contractAddresses';
import KalaKrutNFTABI from '@/abis/KalaKrutNFT.json';

export const useArtists = () => {
  const [artists, setArtists] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { notify } = useToast();
  const { addLog } = useLogger();
  const { provider, isConnected } = useWallet();

  const fetchArtists = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    addLog("Starting artist data fetch.", LogLevel.INFO, LogCategory.DATA);

    if (provider && isConnected) {
      try {
        const nftContract = new ethers.Contract(contractAddresses.KalaKrutNFT, KalaKrutNFTABI.abi, provider);
        const transferFilter = nftContract.filters.Transfer(null, null, null);
        const transferEvents = await nftContract.queryFilter(transferFilter);

        const artistAddresses = [...new Set(
          transferEvents.map(event => (event as EventLog).args?.to).filter(Boolean)
        )];
        
        const artistPromises = artistAddresses.map(async (address: string) => {
          const response = await fetch(`${API_BASE_URL}/api/users?walletAddress=${address}&role=ARTIST`, { credentials: 'include' });
          if (response.ok) return response.json();
          return { id: address, walletAddress: address, name: 'On-Chain Artist', role: UserRole.ARTIST, artistProfile: { bio: 'N/A', genre: 'N/A' } };
        });

        const fetchedArtists = (await Promise.all(artistPromises)).flat().filter(a => a);
        setArtists(fetchedArtists);
        addLog(`Fetched ${fetchedArtists.length} on-chain artists.`, LogLevel.SUCCESS, LogCategory.DATA);
        setIsLoading(false);
        return;
      } catch (e: unknown) {
        const onChainError = "Could not load on-chain artist data. Falling back to API.";
        addLog(`On-chain fetch failed: ${(e as Error).message}`, LogLevel.ERROR, LogCategory.DATA);
        setError(onChainError);
        notify(onChainError, 'warn');
      }
    }

    // Fallback to Off-Chain API
    try {
      let apiData;
      const response = await fetch(`${API_BASE_URL}/api/users?role=ARTIST`, { credentials: 'include' });

      if (response.ok) {
        apiData = await response.json();
        addLog(`Fetched ${apiData.length} artists from REAL API as fallback.`, LogLevel.INFO, LogCategory.API);
      } else {
        addLog(`Real API fallback failed, using mock data.`, LogLevel.WARN, LogCategory.API);
        const mockResponse = await fetch('/artists.json');
        if (!mockResponse.ok) throw new Error('Real API and mock data both failed.');
        apiData = await mockResponse.json();
        notify('Displaying mock data. Wallet not connected and API failed.', 'warn');
      }
      
      if (Array.isArray(apiData)) {
        setArtists(apiData);
      } else {
        throw new Error('Fetched off-chain data is not an array.');
      }

    } catch (e: unknown) {
      const fallbackError = `Critical: Failed to load any artist data: ${(e as Error).message}`;
      addLog(fallbackError, LogLevel.CRITICAL, LogCategory.API);
      setError(fallbackError);
      notify(fallbackError, 'error');
    } finally {
      setIsLoading(false);
    }
  }, [provider, isConnected, notify, addLog]);

  useEffect(() => {
    fetchArtists();
  }, [fetchArtists]);

  return { artists, isLoading, error, refetch: fetchArtists };
};
